import { AxiosResponse } from 'axios';
import { apiClient } from '@/utils';
import { flatten } from 'lodash';
import { CustomField, CustomFieldValueType, Service, PriceTimeUnit, Time, LicenseType, LicensePrice, License, LicensePlanId } from '../types';
import {
    format,
    isSameDay,
    subDays,
    subHours,
    subMinutes,
    setHours,
    setMinutes,
    getHours,
    getMinutes,
    toDate,
    eachDayOfInterval,
    addWeeks,
    getISODay,
    isAfter,
    isBefore,
    isOpenEndedDate,
} from './datetime';
import { RequirementData } from '@/components/Loader';
import { intervalToDuration } from 'date-fns';
import { SemanticICONS } from 'semantic-ui-react';
import { stdnum } from 'stdnum';
import { TranslateFunction } from 'react-localize-redux';

interface ParsedSeconds {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

export function parseSeconds(inputSeconds: number): ParsedSeconds {
    let seconds = inputSeconds;
    var days = Math.floor(inputSeconds / (3600 * 24));
    seconds -= days * 3600 * 24;
    var hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    var minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;

    return { days, hours, minutes, seconds };
}

export function formattedFromTo(
    from: Date | string,
    to: Date | string,
    includeDayIfSameDay: boolean = true
): string {
    const fromDate = toDate(from);
    const toDateObj = toDate(to);

    if (isSameDay(fromDate, toDateObj)) {
        if (includeDayIfSameDay) {
            return `${format(fromDate, 'PPp')} - ${format(toDateObj, 'p')}`;
        } else {
            return `${format(fromDate, 'p')}`;
        }
    }

    return `${format(fromDate, 'PPp')} - ${format(toDateObj, 'PPp')}`;
}

// Create an array with all elements in fullArray where all elements
// in selectionsArray have selectionAttribute = true, all other false.
export function createSelectionArrayFromFull(
    fullArray: Array<any> = [],
    selectionsArray: Array<any> = [],
    selectionAttribute: string
): Array<any> {
    return fullArray.map((fullArrayElement) => {
        const selected = selectionsArray.find(
            (selectedElement) => selectedElement.Id === fullArrayElement.Id
        );

        return {
            ...(selected || {}),
            ...fullArrayElement,
            [selectionAttribute]: !!selected,
        };
    });
}

interface PopulatedCustomField {
    Id: number;
    Value: any;
    meta: CustomField;
}

export function populateCustomFields(
    fields: CustomField[] = [],
    customFieldValues: CustomFieldValueType[] = [],
    useDefaultValues: boolean = false
): PopulatedCustomField[] {
    return fields.map((field: CustomField) => {
        const valueObject = customFieldValues.find((valueObj) => valueObj.Id === field.Id);

        let value;

        if (valueObject?.Value) {
            value = valueObject.Value;
        } else if (field.DefaultValue && useDefaultValues) {
            value = field.DefaultValue;
        }

        return {
            Id: field.Id,
            Value: value,
            meta: field,
        };
    });
}

export function lastTimeToUnbook(service: Service, date: Date | string) {
    let lastTimeToUnbookDate = toDate(date);

    lastTimeToUnbookDate = subDays(lastTimeToUnbookDate, service.UnbookBeforeDays || 0);
    lastTimeToUnbookDate = subHours(lastTimeToUnbookDate, service.UnbookBeforeHours || 0);
    lastTimeToUnbookDate = subMinutes(lastTimeToUnbookDate, service.UnbookBeforeMinutes || 0);

    return format(lastTimeToUnbookDate, 'PPp');
}

export const languageCodeToFlagCode = (code: string) => {
    switch (code) {
        case 'en':
            return 'gb';
        case 'sv':
            return 'se';
        default:
            return code;
    }
};

export const getFullLanguageCode = (language: string) => {
    switch (language) {
      case 'sv':
        return 'sv-se';
      case 'no':
        return 'nb-no';
      case 'en':
        return 'en-us';
      case 'fi':
        return 'fi';
      default:
        return 'sv-se';
    }
  }

  export const getTimeZoneFromLanguageCode = (code: string) => {
    switch (code) {
        case 'sv':
          return 'SE';
        case 'no':
          return 'NO';
        case 'en':
          return 'GB';
        case 'fi':
          return 'FI';
        default:
          return 'SE';
      }
  }

// Use it for nested objects inside querystring for GET requests
export const stringifyNestedParams = (params: Object): string =>
    params ? JSON.stringify(params).replace(/"/gi, '') : '';

export function getRepeatedDates(
    originalDate: Date,
    daysOfWeek: number[] = [],
    numberOfWeeks: number
): Date[] {
    return eachDayOfInterval({
        start: originalDate,
        end: addWeeks(originalDate, numberOfWeeks),
    })
        .filter((date) => daysOfWeek.includes(getISODay(date)))
        .map((date) => setHours(date, getHours(originalDate)))
        .map((date) => setMinutes(date, getMinutes(originalDate)));
}


const TAKE = 300;
export async function getAllItems(initialResponse: AxiosResponse) {
    const take = initialResponse.config.params?.Take || TAKE;
    const total: number = initialResponse.data.Total ?? 0;
    let results: any[] = [];
    while (results.length < total) {
        const config = {
            ...initialResponse.config,
            params: {...initialResponse.config.params, Take: take, Skip: results.length},
        };
        const response: AxiosResponse = await apiClient.request(config);
        const page: any[] = response.data.Results ?? [];
        if (page.length === 0) break;
        results = flatten([results, page]);
    }

    return results;
}

export async function clearSession(clientId: string, refreshToken: string) {
    var myHeaders = new Headers();
    var urlencoded = new URLSearchParams();
    myHeaders.append("Content-Type", "application/x-www-form-urlencoded");
  
    urlencoded.append('refresh_token', refreshToken);
    urlencoded.append("client_id", clientId);
  
    var requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: urlencoded,
    };
  
    return fetch(
      `${process.env.REACT_APP_KEYCLOAK_AUTH_URL}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}/protocol/openid-connect/logout`,
      requestOptions
    );
  }

  export const adaptQueryToLoader = (query: any): RequirementData => {
    return {
        error: query.error as any,
        hasError: query.isError as boolean,
        isLoaded: query.isLoaded as boolean,
        isLoading: query.isLoading as boolean,
        requestIsHidden: false
    }
  }

  export const getVismaIconName = (icon: SemanticICONS): string => {
    switch (icon.toLowerCase()) {
        case 'tasks':
            return 'single-selection';
        case 'envelope':
            return 'email';
        case 'eye':
            return 'preview';
        default:
            return icon.toLowerCase();
    }
  }

  export const getDateWithTime = (minutes: number) => {
    let rootDate = new Date();
    
    const duration = intervalToDuration({ start: 0, end: minutes * 60 * 1000 });

    rootDate.setHours(duration.hours as number);
    rootDate.setMinutes(duration.minutes as number);

    return rootDate;
}

export function isNumeric(str: string | number) {
    if (typeof str != "string") return false // we only process strings!
    // @ts-ignore
    return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
            !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
}

export const formatMinutePriceToTimeUnit = (val: number, unit: PriceTimeUnit) => {
    if(unit === 'hour') {
        return Math.round((val * 60));
    } else if (unit === 'day') {
        return Math.round((val * (60 * 24)));
    }

    return val;
}

export const parseMinutePriceToTimeUnit = (val: number, unit: PriceTimeUnit) => {
    if(unit === 'hour') {
        return (val / 60);
    } else if (unit === 'day') {
        return (val / (60 * 24));
    }

    return val;
}

export const minuteToTimeUnit = (val: number /** in minutes */, unit: PriceTimeUnit): number => {
    if(unit === 'hour') {
        return val / 60;
    } else if (unit === 'day') {
        return val / (60 * 24)
    }

    return val;
};

export const timeUnitToMinute = (val: number, unit: PriceTimeUnit): number => {
    if(unit === 'hour') {
        return val * 60;
    } else if (unit === 'day') {
        return val * (60 * 24)
    }

    return val;
}

export const convertTime = (fromUnit: PriceTimeUnit, toUnit: PriceTimeUnit, value: number): number => {
    if(fromUnit === 'minute' && toUnit === 'hour') {
        return value / 60;
    } else if(fromUnit === 'minute' && toUnit === 'day') {
        return value / (60 * 24)
    } else if(fromUnit === 'hour' && toUnit === 'minute') {
        return value * 60;
    } else if(fromUnit === 'hour' && toUnit === 'day') {
        return value / 24;
    } else if(fromUnit === 'day' && toUnit === 'minute') {
        return value * 60 * 24;
    } else if(fromUnit === 'day' && toUnit === 'hour') {
        return value * 24;
    }

    return value;
}

export const getUnitFromCalculationTypeId = (id: number) : PriceTimeUnit => {
    switch (id) {
        case 1:
            return 'service';
        case 2:
            return 'minute';
        case 3:
            return 'hour';
        case 4:
            return 'day';
        default:
            return 'service';
    }
}

export const formatToLocalCurrencyValue = (value: number, currency = '') => {
    switch (currency) {
        case 'kr':
            return `${value}${currency}`;
        case '$':
            return `${currency}${value}`;
        case '€':
            return `${value}${currency}`;
        default:
            return `${value}${currency}`;
    }
}

const EACCOUNTING_INVOICE_URL_BASE = `${process.env.REACT_APP_EACCOUNTING_API_URL}/#/sales/customerinvoice`;
const EACCOUNTING_INVOICE_DRAFT_URL_BASE = `${process.env.REACT_APP_EACCOUNTING_API_URL}/#/sales/customerinvoicedraft`;

export const getInvoiceUrl = (invoiceId: string, draft = false) => {
    let invoiceBaseUrl = draft ? EACCOUNTING_INVOICE_DRAFT_URL_BASE : EACCOUNTING_INVOICE_URL_BASE;
    
    return `${invoiceBaseUrl}/${invoiceId}`;
}

export function isOrganisationNumberValid(value: string) {
    return (
        // // Sweden
        stdnum.SE.orgnr.validate(value).isValid ||
        stdnum.SE.personnummer.validate(value).isValid ||
        // // Denmark
        stdnum.DK.cpr.validate(value).isValid ||
        stdnum.DK.cvr.validate(value).isValid ||
        // // Norway
        stdnum.NO.orgnr.validate(value).isValid ||
        stdnum.NO.fodselsnummer.validate(value).isValid ||
        // // Finland
        stdnum.FI.hetu.validate(value).isValid ||
        stdnum.FI.ytunnus.validate(value).isValid
    );
}

export function getRebateCodeType(rebateCodeTypeId: number, translate: TranslateFunction) {
    switch (rebateCodeTypeId) {
        case 1:
            return translate('promoCodes.rebateCodeTypeId.1.text');
        case 2:
            return translate('promoCodes.rebateCodeTypeId.2.text');
        case 3:
            return translate('promoCodes.rebateCodeTypeId.3.text');
        case 4:
            return translate('promoCodes.rebateCodeTypeId.4.text');
        case 5:
            return translate('promoCodes.rebateCodeTypeId.5.text');
        default:
            return null;
    }
}

/**
 * How many spots are free in an available-times slot.
 *
 * `Free` and `FreeSpots` are two names for ONE server-side number: every
 * assignment site sets both from the same value, so they can never disagree.
 * `Free` is the canonical one (it is the `IAvailableTime` member the server's
 * own booking code reads), hence the order here. `FreeSpots` remains as a
 * fallback because it is the older name and some callers still carry only it.
 *
 * `undefined` means the slot carries no spot count at all — on the wire both
 * fields are optional, and `{ From, To }` payloads built by the form/store
 * synchronisation chain have neither. It is deliberately NOT 0: "unknown" and
 * "full" must stay distinguishable.
 */
export const freeSpotsOf = (
    time?: { Free?: number; FreeSpots?: number } | null
): number | undefined => {
    const free = time?.Free ?? time?.FreeSpots;
    return typeof free === 'number' && Number.isFinite(free) ? free : undefined;
};

export function stringifyIntervalKey(time: Time, prefix = '', separator = '_') {
    const from = toDate(time.From).toISOString().replace(/\.\d{3}Z$/, 'Z');
    const to = toDate(time.To).toISOString().replace(/\.\d{3}Z$/, 'Z');

    return `${prefix}${from}${separator}${to}`;
}

export function parseIntervalKey(key: string, prefix = '', separator = '_') {
    let interval = []
    if(prefix) {
        const prefixIndex = key.indexOf(prefix);
        interval = key.substring(prefixIndex + prefix.length).split(separator);
    } else {
        interval = key.split(separator)
    }

    return {
        From: interval[0],
        To: interval[1]
    };
}

export const isValidUrl = (url: string) => {
    try {
        new URL(url);
        return true;
    } catch (error) {
        console.warn(error);
        return false
    }
}

export const isSubset = (parentArray: (string|number)[], subsetArray: (string|number)[]) => {
    return subsetArray.every((el) => {
        return parentArray.includes(el)
    })
}

export const groupByPricePlan = (licensesTypes: LicenseType[]) => {
    return licensesTypes.reduce((acc, license) => {

        if(!license.Prices) {
            return acc;
        }
        
        license.Prices.forEach(price => {
            if(price.LicensePlanId && !acc[price.LicensePlanId]) {
                acc[price.LicensePlanId] = [{
                    ...license,
                    Prices: [price]
                }];
            } else if(price.LicensePlanId && acc[price.LicensePlanId]) {
                acc[price.LicensePlanId].push({
                    ...license,
                    Prices: [price]
                })
            }
        });

        return acc;
    }, {} as { [pricePlanName in string | number]: LicenseType[]});
}

export type PlanLike = { Id?: number; VossPlanId?: string; PlanLength?: number; PlanLengthUnit?: string };

// Months in one billing period, from GET /licenses/plans. null = no month count (Week/Day plans).
export const monthsPerPeriod = (plan?: PlanLike | null): number | null => {
    if (!plan?.PlanLength) return null;
    if (plan.PlanLengthUnit === 'Month') return plan.PlanLength;
    if (plan.PlanLengthUnit === 'Year') return plan.PlanLength * 12;
    return null;
};

// Only plans backed by a Voss plan are sellable. Backend VossPlanId is a non-nullable Guid: "none" = empty GUID.
export const hasVossPlan = (plan?: PlanLike | null): boolean =>
    !!plan?.VossPlanId && !/^[0-]+$/.test(plan.VossPlanId);

export const findPlanPrice = (prices: LicensePrice[] | undefined, planId?: number | null) =>
    planId == null ? undefined : prices?.find((p) => p.LicensePlanId === planId);

// Price shown for one plan: per month when the plan length allows, else the full period amount.
export const planDisplayPrice = (amount: number, plan?: PlanLike | null) => {
    const months = monthsPerPeriod(plan);
    return months ? { value: Math.round(amount / months), perMonth: true } : { value: amount, perMonth: false };
};

// Mirrors legacy-api LicenseTypeSets.ApplicationLicenseById (Free, Start, Smart, Pro, Enterprise).
const APPLICATION_LICENSE_TYPE_IDS = [1, 2, 3, 5, 9];

export const isApplicationLicenseTypeId = (typeId: number) => APPLICATION_LICENSE_TYPE_IDS.includes(typeId);

// Per-license plan; fall back to the company billing plan for backfilled rows without one.
export const planOf = (license: License, billingPlanId?: number | null) => license.LicensePlanId ?? billingPlanId;

// Mirrors legacy-api LicensePlanExtensions.IsSellableLicensePlanId: plans with a VOSS binding period.
export const isBindingLicensePlanId = (planId?: number | null): boolean =>
    planId === LicensePlanId.Quarterly || planId === LicensePlanId.Yearly;

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export const hasVossSubscriptionId = (license?: License | null): boolean =>
    !!license?.VossSubscriptionId && license.VossSubscriptionId !== EMPTY_GUID;

// Mirrors legacy-api ResolveAnchorVossSubscriptionIdAsync: the company's current Active, currently
// valid application license with a VOSS subscription, highest TypeId first.
export const findVossAnchorLicense = (licenses: License[], now: Date = new Date()): License | undefined =>
    licenses
        .filter(
            (l) =>
                l.LicenseStatus === 'Active' &&
                isApplicationLicenseTypeId(l.TypeId) &&
                hasVossSubscriptionId(l) &&
                !isAfter(toDate(l.ValidFrom), now) &&
                !isBefore(toDate(l.ValidTo), now)
        )
        .sort((a, b) => b.TypeId - a.TypeId)[0];

// The licenses whose VOSS subscription a cancel of `license` would terminate, most specific first.
// Empty when the backend would end the license immediately (no subscription to resolve).
export const vossSubscriptionHolders = (license: License, licenses: License[], now: Date = new Date()): License[] => {
    const anchor = isApplicationLicenseTypeId(license.TypeId) ? undefined : findVossAnchorLicense(licenses, now);
    if (hasVossSubscriptionId(license)) {
        // The backend never re-anchors an add-on that has its own id; reuse the anchor's date only
        // when it is the same VOSS subscription.
        return anchor && anchor.VossSubscriptionId === license.VossSubscriptionId ? [license, anchor] : [license];
    }
    return anchor ? [anchor] : [];
};

// First future, non-open-ended BindingPeriodEndDate among the holders.
export const bindingPeriodEndDateOf = (holders: License[], now: Date = new Date()): Date | undefined => {
    for (const holder of holders) {
        const raw = holder.VossSubscription?.BindingPeriodEndDate;
        if (!raw || isOpenEndedDate(raw)) continue;
        const date = toDate(raw);
        if (!isNaN(date.getTime()) && isAfter(date, now)) return date;
    }
    return undefined;
};

// Backend treats a lower application type id as a downgrade. A same-type plan change is an upgrade.
export const isLicenseDowngrade = (currentTypeId: number | undefined, nextTypeId: number): boolean =>
    currentTypeId !== undefined &&
    isApplicationLicenseTypeId(currentTypeId) &&
    isApplicationLicenseTypeId(nextTypeId) &&
    nextTypeId < currentTypeId;
